/*
 * File: PageHeader.tsx
 * Project: rosita
 * -----
 * Last Modified: 18 Feb 2022 4:37:54 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Col, Row, Space, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { ReactComponent as MdsiLogoDark } from 'assets/mdsi-logo-dark.svg';
import { ReactComponent as MdsiLogo } from 'assets/mdsi-logo.svg';
import { NavSearch } from 'components/atoms/NavSearch';
import { PhoneNavMenu } from 'components/atoms/PhoneNavMenu';
import { ThemeToggle } from 'components/atoms/ThemeToggle';
import Cookies from 'js-cookie';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'redux/store';
import { UserDropdown } from './UserDropdown';

export const PageHeader = (): JSX.Element => {
  const { user, theme } = useAppSelector((state) => state.app);
  const { xs, sm, md, lg } = useBreakpoint();
  const { pathname } = useLocation();
  const isInternal = pathname.includes('/internal-applications');
  const isWarehouse = pathname.includes('/warehouse-applications');
  const isAbout = pathname.includes('/about');

  // Define responsive flags for easier use - fixing the breakpoint logic
  const isMobile = !!xs;
  const isDesktop = !!md && !lg;

  // State to track customer cookie presence
  const [hasCustomerCookie, setHasCustomerCookie] = useState<boolean>(false);
  // Reference to track the last cookie value
  const lastCookieValue = useRef<string | null>(null);

  // Check for cookie on initial load and when pathname changes
  useEffect(() => {
    const customerCookie = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`);
    setHasCustomerCookie(!!customerCookie);
    lastCookieValue.current = customerCookie || null;
  }, [pathname]);

  // Set up a cookie change listener
  useEffect(() => {
    // Function to check for cookie changes
    const checkCookieChanges = () => {
      const currentCookieValue = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`) || null;

      // Only update if the cookie value has actually changed
      if (currentCookieValue !== lastCookieValue.current) {
        lastCookieValue.current = currentCookieValue;
        setHasCustomerCookie(!!currentCookieValue);
      }
    };

    // Check frequently for cookie changes
    const intervalId = setInterval(checkCookieChanges, 200); // 200ms interval

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Determine search placeholder based on customer cookie presence and current path
  let searchPlaceholder = 'Search Customers';

  if (hasCustomerCookie) {
    searchPlaceholder = 'Search Applications';
  }

  if (isInternal) {
    searchPlaceholder = 'Search Internal Applications';
  } else if (isWarehouse) {
    searchPlaceholder = 'Search Warehouse Applications';
  }

  // Define colors based on theme
  const headerBgColor = theme === 'dark' ? '#1f1e2e' : '#f0f2f5';
  const headerBorderColor = theme === 'dark' ? '#2a293b' : '#e8e8e8';
  const textColor = theme === 'dark' ? '#84859a' : '#595959';

  // Calculate responsive header height
  const headerHeight = isMobile ? 80 : 110;

  const styles: InlineStylesModel = {
    container: {
      alignItems: 'center',
      flex: 1
    },
    appName: {
      display: 'flex',
      cursor: 'pointer',
      color: textColor,
      alignItems: 'center',
      flex: 1
    },
    mainContainer: {
      color: theme === 'dark' ? 'white' : '#000000'
    },
    link: {
      color: textColor
    },
    userMenu: {
      fontSize: '15px'
    },
    searchContainer: {
      flex: '1 1 auto',
      maxWidth: isMobile ? '100%' : 'auto',
      paddingLeft: isMobile ? '12px' : '24px',
      paddingRight: isMobile ? '12px' : 0
    },
    userContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center'
    }
  };

  return (
    <div style={{ height: headerHeight, color: textColor, backgroundColor: headerBgColor, borderBottom: `1px solid ${headerBorderColor}` }}>
      <Row style={{ width: '100%', padding: isMobile ? '10px 16px' : '0px 0px', height: '100%' }} align={'middle'}>
        <Col
          xs={24}
          sm={8}
          md={5}
          lg={4}
          xl={3}
          xxl={3}
          style={{
            borderRight: !isMobile ? `1px solid ${headerBorderColor}` : 'none',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: isMobile ? 0 : 20,
            marginBottom: isMobile ? '8px' : 0
          }}>
          <Row justify="space-between" align="middle" style={{ width: '100%' }}>
            <Space size={10}>
              <a href={process.env.REACT_APP_ERNIE_REDIRECT_URL} style={styles.link}>
                {theme !== 'dark' ? (
                  <MdsiLogoDark height={isMobile ? '26' : isDesktop ? '35' : '25'} width={isMobile ? '75' : isDesktop ? '100' : '78'} />
                ) : (
                  <MdsiLogo height={isMobile ? '26' : isDesktop ? '35' : '25'} width={isMobile ? '75' : isDesktop ? '100' : '78'} />
                )}
              </a>

              <a href={process.env.REACT_APP_ERNIE_REDIRECT_URL}>
                <Typography.Paragraph style={{ fontWeight: 'normal', color: textColor, margin: 0, fontSize: isMobile ? '20px' : isDesktop ? '18px' : '20px' }}>Acuity</Typography.Paragraph>
              </a>
            </Space>
            {isMobile && (
              <Col>
                <PhoneNavMenu />
              </Col>
            )}
          </Row>
        </Col>

        <Col
          xs={24}
          sm={16}
          md={19}
          lg={20}
          xl={21}
          xxl={21}
          style={{
            height: isMobile ? 'auto' : '100%',
            display: 'flex',
            alignItems: 'center',
            paddingRight: isMobile ? 0 : 20
          }}>
          <Row
            justify="space-between"
            align="middle"
            style={{
              width: '100%',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
            {!isMobile && <Col style={styles.searchContainer}>{!isAbout && <NavSearch placeholder={searchPlaceholder} />}</Col>}
            {!isMobile && (
              <Col style={styles.userContainer}>
                <Space size={15}>
                  <ThemeToggle />
                  <Typography.Text style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)', fontSize: '16px' }}>{user?.name?.toUpperCase()}</Typography.Text>
                  <UserDropdown />
                </Space>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};
